/* eslint-disable max-lines */
import {
  ClockIcon,
} from '@heroicons/react/24/outline';
import {
  ScrollShadow,
  Skeleton,
  Tooltip,
} from '@heroui/react';
import type { TFunction } from 'i18next';
import {
  ArrowRight, ChevronRight, PanelLeft,
} from 'lucide-react';
import React, {
  FC, useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ReactSVG } from 'react-svg';

import { useIsMobile, useLocalStorage } from '@/hooks';
import { useOrganizationState } from '@/state';
import { LocaleLink } from '@/utils/locale';

import shortLogo from '../../../assets/images/small-logo.svg?url';
import { ReactComponent as LogoHotelspot } from '../../../assets/logo-hs.svg';
import type {
  IconProp, MenuItemGroup, MenuItemProps, MenuStatus, SubitemProps,
} from '../../../types';
import { useHelpAiChat } from '../../../views/Dashboard/components/HelpAiChat';
import { Flex } from '../../Flex';
import { Icon } from '../../Icon';
import { OrganizationSelector } from '../../OrganizationSelector';
import {
  CollapseButton,
  FlyoutPanel,
  FlyoutSubLink,
  GroupChevron,
  LanguageBar,
  LanguageBarText,
  LogoContainer,
  LogoHoverOverlay,
  MenuGroupButton,
  MenuIconWrapper,
  MenuItemsWrapper,
  MenuLink,
  MenuLinkIcoWrapper,
  MenuLinkText,
  MenuLinkWrapper,
  MobileCloseButton,
  MobileOverlay,
  OrgSelectorRow,
  SectionLabel,
  SectionRule,
  SidebarFooter,
  SidebarLogoWrapper,
  SidebarWrapper,
  SubmenuAccordion,
  SubmenuClip,
  SubmenuInner,
  SubmenuLink,
} from './Sidebar.styled';

/**
 * Etykieta pozycji menu. Konfiguracja niesie klucz i18n, a wpisany w niej tekst zostaje
 * jako zapas, więc dopisanie pozycji bez klucza niczego nie psuje.
 */
const menuLabel = (
  entry: { labelKey?: string; pathName?: string; dividerLabel?: string },
  translate: TFunction,
): string => {
  const fallback = entry.pathName ?? entry.dividerLabel ?? '';
  return entry.labelKey ? String(translate(entry.labelKey, fallback)) : fallback;
};

/** Numer wersji panelu — z `package.json` (Vite `define`). */
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';

export interface SidebarProps {
  menuConfig?: MenuItemProps[];
  showMobile?: boolean;
  setShowMobile: (isOpen: boolean) => void;
}

const statusConfig = {
  new: { gradient: 'from-red-500 to-rose-500', label: 'New', icon: null },
  updated: { gradient: 'from-blue-500 to-indigo-500', label: 'Updated', icon: null },
  soon: { gradient: 'from-gray-700 to-gray-900', label: 'Soon', icon: null },
  deprecated: { gradient: 'from-orange-500 to-amber-500', label: 'Deprecated', icon: ClockIcon },
} as const;

const StatusBadge: FC<{ status: MenuStatus }> = ({ status }) => {
  const config = statusConfig[status];
  const IconComponent = config.icon;

  return (
    <span
      className={`
        px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap shadow-sm ml-auto
        bg-gradient-to-r ${config.gradient} text-white
        ${IconComponent ? 'flex items-center justify-center' : ''}
      `}
    >
      {IconComponent ? (
        <IconComponent className="w-3 h-3" />
      ) : (
        config.label
      )}
    </span>
  );
};

const renderIcon = (icon?: IconProp, collapsed?: boolean) => {
  if (!icon) return null;
  return (
    <MenuLinkIcoWrapper $collapsed={!!collapsed}>
      {typeof icon === 'string' ? (
        <Icon name={icon} size={16} color="currentColor" />
      ) : (
        <div className="w-[16px] h-[16px] flex items-center justify-center" style={{ strokeWidth: 2 }}>
          {icon}
        </div>
      )}
    </MenuLinkIcoWrapper>
  );
};

// Liczba pełnych segmentów `target`, jeśli występują jako ciągły zestaw w
// `pathname` (z dowolnym prefiksem locale, np. /pl/, i opcjonalnymi sub-routami);
// inaczej 0. Dzięki temu '/product-rental' NIE matchuje '/product-rental-reservations'
// (substring-match dawał błędne podświetlenie), a dłuższe dopasowanie wygrywa —
// więc aktywny jest dokładnie jeden, najbardziej szczegółowy wpis.
const segmentMatchLength = (pathname: string, target: string): number => {
  const segs = pathname.split('/').filter(Boolean);
  const tsegs = target.split('/').filter(Boolean);
  if (tsegs.length === 0) return 0;
  for (let i = 0; i + tsegs.length <= segs.length; i += 1) {
    if (tsegs.every((seg, j) => segs[i + j] === seg)) return tsegs.length;
  }
  return 0;
};

// Flyout panel (popover) renderowany do portal — pokazuje subitemy grupy po
// najechaniu na rodzica. Pozycja absolutna względem viewport-a, bo sidebar
// ma `overflow: hidden` i panel "wystaje" poza jego boczną krawędź.
interface FlyoutProps {
  group: MenuItemGroup;
  anchorRect: DOMRect;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onItemClick: () => void;
}

const Flyout: FC<FlyoutProps> = ({
  group, anchorRect, onClose, onMouseEnter, onMouseLeave, onItemClick,
}) => {
  const { t } = useTranslation();

  // Klikamy w subitem → zamykamy flyout i (na mobile) drawer.
  const handleSubItemClick = () => {
    onItemClick();
    onClose();
  };

  // Pozycja: zaraz na prawo od wiersza w sidebarze, wyrównana do góry wiersza.
  // Jeśli panel nie zmieści się w pionie, podciągany jest do góry tak, żeby
  // mieścił się w viewport (8px margines).
  const top = Math.max(8, Math.min(anchorRect.top, window.innerHeight - 80));
  const left = anchorRect.right + 6;

  return createPortal(
    <FlyoutPanel
      style={{ top, left }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {group.subItem.map((sub: SubitemProps) => (
          <FlyoutSubLink
            key={sub.path}
            to={sub.path}
            end={sub.path === '/'}
            onClick={handleSubItemClick}
          >
            {sub.icon && (
              <span style={{ display: 'inline-flex', width: 16, height: 16, alignItems: 'center', justifyContent: 'center' }}>
                {typeof sub.icon === 'string'
                  ? <Icon name={sub.icon} size={14} color="#fff" />
                  : sub.icon}
              </span>
            )}
            <span>{menuLabel(sub, t)}</span>
            {sub.status && <StatusBadge status={sub.status} />}
          </FlyoutSubLink>
        ))}
      </div>
    </FlyoutPanel>,
    document.body,
  );
};

export const Sidebar: FC<SidebarProps> = ({
  menuConfig,
  showMobile,
  setShowMobile,
}) => {
  const [isCollapsedStorage, setIsCollapsed] = useLocalStorage('isMenuCollapsed', false);
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const location = useLocation();
  const isMobileOrTablet = useIsMobile();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Stan dla aktywnego flyoutu (tryb zwiniętego sidebara) — id grupy + DOMRect rodzica.
  const [activeFlyout, setActiveFlyout] = useState<{ id: string; rect: DOMRect; group: MenuItemGroup } | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  // Stan accordion (tryb rozwiniętego sidebara) — single-open: najwyżej jedna
  // grupa rozwinięta naraz (ścieżka grupy albo null).
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  const toggleGroup = (path: string): void => {
    setExpandedGroup((prev) => (prev === path ? null : path));
  };

  // Najlepiej (najdłużej po segmentach) dopasowany sub-item w całym menu — dzięki
  // temu aktywny jest dokładnie jeden wpis (najbardziej szczegółowy), a nie np.
  // '/special-offer' równocześnie z '/special-offer/new'.
  const { activeSubPath, activeGroupPath } = useMemo(() => {
    let subPath: string | null = null;
    let groupPath: string | null = null;
    let bestLen = 0;
    (menuConfig ?? []).forEach((item) => {
      if (item.kind !== 'group') return;
      item.subItem.forEach((sub) => {
        const len = segmentMatchLength(location.pathname, sub.path);
        if (len > bestLen) {
          bestLen = len;
          subPath = sub.path;
          groupPath = item.path;
        }
      });
    });
    return { activeSubPath: subPath, activeGroupPath: groupPath };
  }, [menuConfig, location.pathname]);

  // Gdy dokowany asystent AI „Stefan" jest otwarty, zwijamy sidebar (wzór
  // ElevenLabs — chowamy nawigację, żeby zrobić miejsce na treść + panel czatu).
  const { isOpen: isChatOpen } = useHelpAiChat();

  // On mobile/tablet, always show expanded menu
  const isCollapsed = isMobileOrTablet ? false : (isChatOpen || isCollapsedStorage);

  const handleCollapseClick = (): void => setIsCollapsed(!isCollapsed);

  const closeDrawerIfMobile = (): void => {
    if (showMobile) setShowMobile(false);
  };

  const handleItemClick = (): void => {
    closeDrawerIfMobile();
  };

  // Otwieramy flyout natychmiast po najechaniu, ale zamknięcie ma 150ms opóźnienie,
  // dzięki czemu user może płynnie przesunąć kursor z wiersza na panel.
  const cancelClose = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => {
      setActiveFlyout(null);
      closeTimerRef.current = null;
    }, 150);
  };

  const openFlyout = (group: MenuItemGroup, target: HTMLElement) => {
    cancelClose();
    setActiveFlyout({
      id: group.path,
      rect: target.getBoundingClientRect(),
      group,
    });
  };

  const { isResolved: orgResolved } = useOrganizationState();
  const isSidebarLoading = !orgResolved;

  const { t } = useTranslation();

  useEffect(() => {
    closeDrawerIfMobile();
    setActiveFlyout(null); // zamykamy flyout po zmianie ścieżki
  }, [location.pathname]);

  // Auto-rozwijamy (single-open) grupę zawierającą aktywną ścieżkę po nawigacji,
  // żeby user widział, gdzie jest. Pozostałe grupy się zwijają.
  useEffect(() => {
    if (!activeGroupPath) return;
    setExpandedGroup(activeGroupPath);
  }, [activeGroupPath]);

  useEffect(() => () => {
    cancelClose();
  }, []);

  const renderLeafItem = (menuItem: Extract<MenuItemProps, { kind: 'item' }>) => {
    const isSoon = menuItem.status === 'soon';
    const tourId = menuItem.path === '/' ? 'dashboard' : menuItem.path.replace(/^\//, '').replace(/\//g, '-');

    return (
      <MenuLink
        key={`item-${menuItem.path}-${menuItem.pathName}`}
        to={isSoon ? '#' : menuItem.path}
        end={menuItem.path === '/'}
        $disabled={isSoon}
        $isCollapsed={isCollapsed}
        onClick={isSoon ? (e: any) => e.preventDefault() : handleItemClick}
        onMouseEnter={() => { setActiveFlyout(null); cancelClose(); }}
        data-tour={`sidebar-${tourId}`}
      >
        <MenuLinkWrapper $collapsed={isCollapsed}>
          {isCollapsed ? (
            <Tooltip
              key={`tooltip-${menuItem.pathName}`}
              radius="sm"
              showArrow
              content={(
                <Flex itemAlign="center" gap="8px">
                  {menuLabel(menuItem, t)}
                  {menuItem.status && <StatusBadge status={menuItem.status} />}
                </Flex>
              )}
              placement="right"
              classNames={{
                base: 'pointer-events-auto',
                content: 'bg-white dark:bg-[#242a33] text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-[#374151] shadow-lg',
              }}
            >
              <MenuIconWrapper>
                {renderIcon(menuItem.icon, true)}
              </MenuIconWrapper>
            </Tooltip>
          ) : (
            <MenuIconWrapper>
              {renderIcon(menuItem.icon, false)}
            </MenuIconWrapper>
          )}
          {!isCollapsed && <MenuLinkText>{menuLabel(menuItem, t)}</MenuLinkText>}
          {!isCollapsed && menuItem.status && <StatusBadge status={menuItem.status} />}
        </MenuLinkWrapper>
      </MenuLink>
    );
  };

  // Tryb zwinięty (72px): subitemy w hover-flyoucie, bo accordion inline nie
  // ma się gdzie zmieścić (widać tylko ikonę).
  const renderCollapsedGroupItem = (menuItem: MenuItemGroup) => {
    const isSoon = menuItem.status === 'soon';
    const tourId = menuItem.path.replace(/^\//, '').replace(/\//g, '-');
    const isActive = activeFlyout?.id === menuItem.path;

    const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
      if (isSoon) {
        e.preventDefault();
        return;
      }
      if (isActive) {
        setActiveFlyout(null);
      } else {
        openFlyout(menuItem, e.currentTarget as HTMLElement);
      }
    };

    return (
      <MenuGroupButton
        key={`item-${menuItem.path}-${menuItem.pathName}`}
        type="button"
        $disabled={isSoon}
        $isCollapsed
        $isActive={isActive}
        onClick={handleClick}
        onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => openFlyout(menuItem, e.currentTarget)}
        onMouseLeave={scheduleClose}
        onFocus={(e: React.FocusEvent<HTMLButtonElement>) => openFlyout(menuItem, e.currentTarget)}
        data-tour={`sidebar-${tourId}`}
      >
        <MenuLinkWrapper $collapsed>
          <Tooltip
            key={`tooltip-${menuItem.pathName}`}
            radius="sm"
            showArrow
            content={(
              <Flex itemAlign="center" gap="8px">
                {menuLabel(menuItem, t)}
                {menuItem.status && <StatusBadge status={menuItem.status} />}
              </Flex>
            )}
            placement="right"
            classNames={{
              base: 'pointer-events-auto',
              content: 'bg-white dark:bg-[#242a33] text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-[#374151] shadow-lg',
            }}
          >
            <MenuIconWrapper>{renderIcon(menuItem.icon, true)}</MenuIconWrapper>
          </Tooltip>
        </MenuLinkWrapper>
      </MenuGroupButton>
    );
  };

  // Tryb rozwinięty: klik w grupę rozwija/zwija accordion z subitemami (bez ikon).
  const renderExpandedGroupItem = (menuItem: MenuItemGroup) => {
    const isSoon = menuItem.status === 'soon';
    const tourId = menuItem.path.replace(/^\//, '').replace(/\//g, '-');
    const isExpanded = expandedGroup === menuItem.path;
    const hasActiveChild = menuItem.subItem.some((sub) => sub.path === activeSubPath);

    const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
      if (isSoon) {
        e.preventDefault();
        return;
      }
      toggleGroup(menuItem.path);
    };

    return (
      <React.Fragment key={`group-${menuItem.path}-${menuItem.pathName}`}>
        <MenuGroupButton
          type="button"
          $disabled={isSoon}
          $isCollapsed={false}
          $isActive={hasActiveChild}
          onClick={handleClick}
          aria-expanded={isExpanded}
          data-tour={`sidebar-${tourId}`}
        >
          <MenuLinkWrapper $collapsed={false}>
            <MenuIconWrapper>{renderIcon(menuItem.icon, false)}</MenuIconWrapper>
            <MenuLinkText>{menuLabel(menuItem, t)}</MenuLinkText>
            {menuItem.status && <StatusBadge status={menuItem.status} />}
            <GroupChevron $expanded={isExpanded}>
              <ChevronRight size={11} />
            </GroupChevron>
          </MenuLinkWrapper>
        </MenuGroupButton>

        <SubmenuAccordion $expanded={isExpanded}>
          <SubmenuClip>
            <SubmenuInner>
              {menuItem.subItem.map((sub: SubitemProps) => {
                const subIsSoon = sub.status === 'soon';
                return (
                  <SubmenuLink
                    key={sub.path}
                    to={subIsSoon ? '#' : sub.path}
                    end={sub.path === '/'}
                    $disabled={subIsSoon}
                    $active={sub.path === activeSubPath}
                    tabIndex={isExpanded ? undefined : -1}
                    onClick={subIsSoon ? (e: React.MouseEvent) => e.preventDefault() : handleItemClick}
                  >
                    {menuLabel(sub, t)}
                    {sub.status && <StatusBadge status={sub.status} />}
                  </SubmenuLink>
                );
              })}
            </SubmenuInner>
          </SubmenuClip>
        </SubmenuAccordion>
      </React.Fragment>
    );
  };

  const renderGroupItem = (menuItem: MenuItemGroup) => (
    isCollapsed ? renderCollapsedGroupItem(menuItem) : renderExpandedGroupItem(menuItem)
  );

  // Grupy są top-levelowymi wpisami z własnym flyoutem, a `divider` dzieli je na
  // sekcje: rozwinięty sidebar pokazuje etykietę, zwinięty samą kreskę.
  const renderMenuItems = () => {
    if (!menuConfig || menuConfig.length === 0) return null;
    return menuConfig.map((item) => {
      if (item.kind === 'group') return renderGroupItem(item);
      if (item.kind === 'item') return renderLeafItem(item);
      if (item.kind === 'divider') {
        return isCollapsed
          ? <SectionRule key={`divider-${item.dividerLabel}`} />
          : <SectionLabel key={`divider-${item.dividerLabel}`}>{menuLabel(item, t)}</SectionLabel>;
      }
      return null;
    });
  };

  return (
    <>
      <MobileOverlay $isOpen={showMobile} onClick={() => setShowMobile(false)} />
      <SidebarWrapper $collapsed={Boolean(isCollapsed)} $isMobileMenuOpen={showMobile}>
        {isSidebarLoading
          ? (
            <Flex itemAlign="center" justifyContent="space-between">
              <Skeleton className="rounded-md" style={{ margin: '12px 24px', width: '36px', height: '36px' }} />
              <Skeleton className="rounded-md" style={{ margin: '12px 24px', width: '20px', height: '20px' }} />
            </Flex>
          )
          : (
            <SidebarLogoWrapper>
              {isCollapsed ? (
                <Flex itemAlign="center" justifyContent="center">
                  <LogoContainer
                    onClick={handleCollapseClick}
                    onMouseEnter={() => setIsLogoHovered(true)}
                    onMouseLeave={() => setIsLogoHovered(false)}
                    $isCollapsed
                  >
                    <div style={{ opacity: isLogoHovered ? 0 : 1, transition: 'opacity 0.2s ease' }}>
                      <ReactSVG src={shortLogo} shapeRendering={1} />
                    </div>
                    {isLogoHovered && (
                      <LogoHoverOverlay>
                        <PanelLeft size={18} strokeWidth={2} />
                      </LogoHoverOverlay>
                    )}
                  </LogoContainer>
                </Flex>
              ) : (
                <>
                  <LocaleLink to="/">
                    <LogoHotelspot />
                  </LocaleLink>
                  <CollapseButton
                    onClick={handleCollapseClick}
                    onMouseEnter={() => setIsLogoHovered(true)}
                    onMouseLeave={() => setIsLogoHovered(false)}
                  >
                    <PanelLeft size={18} strokeWidth={2} />
                  </CollapseButton>
                </>
              )}
              {showMobile && (
                <MobileCloseButton onClick={() => setShowMobile(false)}>
                  <ArrowRight size={18} />
                </MobileCloseButton>
              )}
            </SidebarLogoWrapper>
          )}

        <OrgSelectorRow $isCollapsed={isCollapsed}>
          <OrganizationSelector isCollapsed={isCollapsed} />
        </OrgSelectorRow>

        <ScrollShadow ref={scrollRef} className="w-[100%] flex-1 min-h-0" size={1} hideScrollBar>
          <MenuItemsWrapper>
            {/* eslint-disable-next-line no-nested-ternary */}
            {isSidebarLoading ? (
              <div className="flex flex-col gap-3 px-3 pt-3">
                {Array.from({ length: 9 }).map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="rounded-md" style={{ width: 20, height: 20 }} />
                    {!isCollapsed && <Skeleton className="h-[14px] w-[80%] rounded" />}
                  </div>
                ))}
              </div>
            ) : menuConfig && menuConfig.length ? (
              renderMenuItems()
            ) : (
              <Flex itemAlign="center" justifyContent="center">
                <p className="text-red-500">ERROR!</p>
              </Flex>
            )}
          </MenuItemsWrapper>
        </ScrollShadow>

        {!isSidebarLoading && (
          <SidebarFooter $isCollapsed={isCollapsed}>
            <Tooltip
              radius="sm"
              showArrow
              placement="right"
              content={(
                <span style={{ fontSize: 12 }}>
                  {t('sidebar.version', 'Wersja {{version}}', { version: APP_VERSION })}
                </span>
              )}
              classNames={{
                content: 'bg-white dark:bg-[#242a33] text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-[#374151] shadow-lg',
              }}
            >
              <LanguageBar $isCollapsed={isCollapsed}>
                {!isCollapsed ? (
                  <LanguageBarText>
                    {t('sidebar.version', 'Wersja {{version}}', { version: APP_VERSION })}
                  </LanguageBarText>
                ) : (
                  <LanguageBarText>{APP_VERSION}</LanguageBarText>
                )}
              </LanguageBar>
            </Tooltip>
          </SidebarFooter>
        )}
      </SidebarWrapper>

      {activeFlyout && (
        <Flyout
          group={activeFlyout.group}
          anchorRect={activeFlyout.rect}
          onClose={() => setActiveFlyout(null)}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          onItemClick={handleItemClick}
        />
      )}
    </>
  );
};
